import { computed, inject, Injectable, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ApiError } from '../../models/errors/api-error.model';
import { CartRepositoryService } from '../../repositories/ecommerce/cart.repository.service';
import {
  AddressInModel,
  AddressOutModel,
  AddressUpdateModel,
  BillingAddressSelectionModel,
  CartResponseModel,
  CheckoutInModel,
  CheckoutResponseModel,
  CustomerInModel,
  CartCouponModel,
  NfarmConfigurationInModel,
  PaymentMethodModel,
} from '../../models/ecommerce/cart.model';

const CART_TOKEN_STORAGE_KEY = 'nfarm_cart_token';

@Injectable({
    providedIn: 'root',
})
export class CartService {
    private readonly repo = inject(CartRepositoryService);

    private readonly cartTokenSignal = signal<string | null>(
        localStorage.getItem(CART_TOKEN_STORAGE_KEY)
    );
    private readonly cartSignal = signal<CartResponseModel | null>(null);
    private readonly loadingSignal = signal<boolean>(false);
    private readonly errorSignal = signal<unknown>(null);

    private readonly restorePromise: Promise<void> | null;

    readonly cart = computed(() => this.cartSignal());
    readonly cartToken = computed(() => this.cartTokenSignal());
    readonly isLoading = computed(() => this.loadingSignal());
    readonly error = computed(() => this.errorSignal());
    readonly coupon = computed<CartCouponModel | null>(() => this.cartSignal()?.coupon ?? null);
    readonly pricingRevision = computed(() => this.cartSignal()?.pricing_revision ?? null);

    constructor() {
        const existingToken = this.cartTokenSignal();
        this.restorePromise = existingToken ? this.restoreCart(existingToken) : null;
    }

    public async ensureCart(): Promise<CartResponseModel> {
        if (this.restorePromise) {
            await this.restorePromise;
        }

        const currentCart = this.cartSignal();
        if (currentCart) {
            return currentCart;
        }

        this.loadingSignal.set(true);
        this.errorSignal.set(null);
        try {
            const cart = await firstValueFrom(this.repo.createCart());
            this.cartSignal.set(cart);
            this.persistToken(cart.cart_token ?? null);
            return cart;
        } catch (error) {
            this.errorSignal.set(error);
            throw error;
        } finally {
            this.loadingSignal.set(false);
        }
    }

    public async setNfarmConfiguration(
        payload: NfarmConfigurationInModel
    ): Promise<CartResponseModel> {
        await this.ensureCart();
        const updated = await firstValueFrom(
            this.repo.setNfarmConfiguration(this.requireToken(), payload)
        );
        this.cartSignal.set(updated);
        return updated;
    }

    public async setCustomer(payload: CustomerInModel): Promise<CartResponseModel> {
        const updated = await firstValueFrom(this.repo.setCustomer(this.requireToken(), payload));
        this.cartSignal.set(updated);
        return updated;
    }

    public async listBillingAddresses(): Promise<AddressOutModel[]> {
        return firstValueFrom(this.repo.listBillingAddresses(this.requireToken()));
    }

    public async createBillingAddress(payload: AddressInModel): Promise<AddressOutModel> {
        return firstValueFrom(this.repo.createBillingAddress(this.requireToken(), payload));
    }

    public async updateBillingAddress(
        addressId: number,
        payload: AddressUpdateModel
    ): Promise<AddressOutModel> {
        return firstValueFrom(this.repo.updateBillingAddress(this.requireToken(), addressId, payload));
    }

    public async deleteBillingAddress(addressId: number): Promise<void> {
        await firstValueFrom(this.repo.deleteBillingAddress(this.requireToken(), addressId));
    }

    public async selectBillingAddress(
        payload: BillingAddressSelectionModel
    ): Promise<CartResponseModel> {
        const updated = await firstValueFrom(
            this.repo.selectBillingAddress(this.requireToken(), payload)
        );
        this.cartSignal.set(updated);
        return updated;
    }

    // --- Coupon -------------------------------------------------------------

    /**
     * Il server non ripulisce il codice: " promo20 " viene rifiutato (prova su
     * QA del 27/10/2026), quindi gli spazi si tolgono qui. Un rifiuto lascia il
     * carrello invariato e risale al chiamante come `ApiError`.
     */
    public async applyCoupon(code: string): Promise<CartResponseModel> {
        const updated = await firstValueFrom(
            this.repo.applyCoupon(this.requireToken(), { code: code.trim() })
        );
        this.cartSignal.set(updated);
        return updated;
    }

    public async removeCoupon(): Promise<CartResponseModel> {
        const updated = await firstValueFrom(this.repo.removeCoupon(this.requireToken()));
        this.cartSignal.set(updated);
        return updated;
    }

    /**
     * Ricalcola il carrello mantenendo il coupon: la guida chiede di ripetere il
     * PUT della configurazione corrente. La sola lettura del carrello non
     * aggiorna i prezzi, anche quando il coupon risulta scaduto.
     */
    public async repriceCart(): Promise<CartResponseModel | null> {
        const config = this.cartSignal()?.nfarm_configuration;
        if (!config) {
            return this.cartSignal();
        }
        return this.setNfarmConfiguration({
            plan_code: config.plan_code,
            company_count: config.company_count,
            surface_hectares: config.surface_hectares,
            operator_count: config.operator_count,
            dss_codes: config.dss_codes,
            no_limit: config.no_limit,
            is_trial: config.is_trial,
        });
    }

    /**
     * Aggiunge la revisione del riepilogo a un body di pagamento. Solo con un
     * coupon il server la produce, e senza coupon gli acquisti continuano a
     * funzionare senza: un `null` non va inviato.
     */
    public withPricingRevision<T extends { pricing_revision?: string }>(payload: T): T {
        const revision = this.pricingRevision();
        return revision ? { ...payload, pricing_revision: revision } : payload;
    }

    /**
     * `CART_REVIEW_REQUIRED` porta il carrello ricalcolato: va mostrato al
     * posto di quello vecchio, e il nuovo tentativo spetta al cliente. Un
     * nuovo invio automatico confermerebbe importi che non ha visto.
     */
    public adoptCartFromError(error: unknown): boolean {
        if (!(error instanceof ApiError) || !isCartPayload(error.cart)) {
            return false;
        }
        this.cartSignal.set(error.cart);
        return true;
    }

    public async getPaymentMethods(): Promise<PaymentMethodModel[]> {
        const response = await firstValueFrom(this.repo.getPaymentMethods(this.requireToken()));
        return response.methods;
    }

    /**
     * Conferma l'ordine. Non svuota il carrello: con un pagamento che può
     * fallire o restare in sospeso, chi ha appena confermato deve poter
     * ritentare. Lo svuotamento è esplicito e spetta al chiamante, quando il
     * flusso è davvero concluso.
     *
     * Il checkout non rilascia sessioni (contratto stage 09/09/2026): l'area
     * ordini si apre solo dopo conferma email e accesso.
     */
    public async checkout(payload: CheckoutInModel): Promise<CheckoutResponseModel> {
        return firstValueFrom(
            this.repo.checkout(this.requireToken(), this.withPricingRevision(payload))
        );
    }

    public clearCart(): void {
        this.persistToken(null);
        this.cartSignal.set(null);
        this.errorSignal.set(null);
    }

    // Riallinea il carrello dal backend: utile dopo operazioni che lo modificano
    // senza restituirlo (es. DELETE di un indirizzo di fatturazione selezionato).
    public async refreshCart(): Promise<CartResponseModel | null> {
        const token = this.cartTokenSignal();
        if (!token) {
            return null;
        }
        await this.restoreCart(token);
        return this.cartSignal();
    }

    /**
     * Rilettura senza stato di caricamento né azzeramento del token: il
     * checkout sparisce dietro lo scheletro mentre `isLoading` è attivo, e un
     * errore appena mostrato andrebbe perso. Un fallimento lascia il carrello
     * com'era.
     */
    public async reloadQuietly(): Promise<void> {
        const token = this.cartTokenSignal();
        if (!token) {
            return;
        }
        try {
            this.cartSignal.set(await firstValueFrom(this.repo.getCart(token)));
        } catch {
            // Resta l'ultimo carrello noto: l'errore originale è già a video.
        }
    }

    private async restoreCart(token: string): Promise<void> {
        this.loadingSignal.set(true);
        this.errorSignal.set(null);
        try {
            const cart = await firstValueFrom(this.repo.getCart(token));
            this.cartSignal.set(cart);
        } catch (error) {
            this.persistToken(null);
            this.cartSignal.set(null);
            this.errorSignal.set(error);
        } finally {
            this.loadingSignal.set(false);
        }
    }

    private persistToken(token: string | null): void {
        this.cartTokenSignal.set(token);
        if (token) {
            localStorage.setItem(CART_TOKEN_STORAGE_KEY, token);
        } else {
            localStorage.removeItem(CART_TOKEN_STORAGE_KEY);
        }
    }

    private requireToken(): string {
        const token = this.cartTokenSignal();
        if (!token) {
            throw new Error('Nessun carrello attivo: chiamare ensureCart() prima di operare sul carrello.');
        }
        return token;
    }
}

/** Controllo minimo sul carrello allegato a un errore, che arriva non tipizzato. */
function isCartPayload(value: unknown): value is CartResponseModel {
    return (
        !!value &&
        typeof value === 'object' &&
        Array.isArray((value as CartResponseModel).lines) &&
        !!(value as CartResponseModel).totals
    );
}
